import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Network, Loader2, X, Plus, Save, Pencil, Check } from "lucide-react";
import { mapSchema, mapEntities, mapGetEntity, mapSetEntity, mapCreateEntity, mapRenameEntity } from "@/lib/api";

const EDITABLE = new Set(["deity", "temple", "category", "term"]);

export default function AdminMappings() {
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("deity");
  const [entities, setEntities] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);       // {type,slug,name}
  const [links, setLinks] = useState({});      // {otherType: [{slug,name}]}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState({ type: "", q: "", items: [] });
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");

  useEffect(() => { mapSchema().then((d) => setTypes(d.types)).catch(() => {}); }, []);
  const refreshEntities = () => mapEntities(type, q).then((d) => setEntities(d.items)).catch(() => {});
  useEffect(() => { refreshEntities(); }, [type, q]);

  const open = async (e) => {
    setLoading(true); setSel(e); setRenaming(false);
    try { const d = await mapGetEntity(e.type, e.slug); setLinks(d.links || {}); }
    finally { setLoading(false); }
  };

  const createEntity = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const d = await mapCreateEntity(type, name);
      setNewName("");
      await refreshEntities();
      toast.success(`Created “${d.name}”.`);
      open({ type: d.type, slug: d.slug, name: d.name });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create."); }
    finally { setCreating(false); }
  };

  const doRename = async () => {
    const name = renameVal.trim();
    if (!name || name === sel.name) { setRenaming(false); return; }
    try {
      const d = await mapRenameEntity(sel.type, sel.slug, name);
      setSel((s) => ({ ...s, name: d.name }));
      setEntities((es) => es.map((x) => (x.slug === sel.slug && x.type === sel.type ? { ...x, name: d.name } : x)));
      setRenaming(false);
      toast.success("Renamed — updated across the platform.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename."); }
  };

  const otherTypes = types.filter((t) => t.type !== sel?.type);

  const removeLink = (ot, slug) => setLinks((l) => ({ ...l, [ot]: (l[ot] || []).filter((x) => x.slug !== slug) }));
  const addLink = (ot, item) => setLinks((l) => {
    const cur = l[ot] || [];
    if (cur.some((x) => x.slug === item.slug)) return l;       // dedupe
    return { ...l, [ot]: [...cur, { slug: item.slug, name: item.name }] };
  });

  const loadPicker = async (ot, query) => {
    const d = await mapEntities(ot, query);
    setPicker({ type: ot, q: query, items: d.items });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(links).forEach(([k, arr]) => { payload[k] = arr.map((x) => x.slug); });
      const d = await mapSetEntity(sel.type, sel.slug, payload);
      setLinks(d.links || {});
      toast.success("Mappings saved — synced across the platform.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-mappings">
      <div className="mb-5 flex items-center gap-3">
        <Network className="h-6 w-6 text-saffron" />
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Mappings</h1>
          <p className="text-sm text-ink-3">One place to link deities, temples, mantras, stotras, pooja vidhis, festivals, tags & terms. Links are bidirectional and deduped.</p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        {/* Entity list */}
        <div className="border border-line bg-surface p-4">
          <div className="flex flex-wrap gap-1.5">
            {types.map((t) => (
              <button key={t.type} onClick={() => { setType(t.type); setSel(null); }} data-testid={`maptype-${t.type}`}
                className={`px-2.5 py-1 text-xs transition-colors ${type === t.type ? "bg-saffron text-white" : "border border-line text-ink-2 hover:border-saffron"}`}>{t.label}</button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="map-search"
            className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm focus:border-saffron focus:outline-none" />
          {EDITABLE.has(type) && (
            <div className="mt-2 flex items-center gap-1.5" data-testid="map-create-row">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createEntity()}
                placeholder={`New ${types.find((t) => t.type === type)?.label?.replace(/s$/, "") || type}…`} data-testid="map-create-input"
                className="min-w-0 flex-1 border border-line bg-parchment px-2 py-1.5 text-sm focus:border-saffron focus:outline-none" />
              <button onClick={createEntity} disabled={creating || !newName.trim()} data-testid="map-create-btn"
                className="inline-flex items-center gap-1 bg-[var(--saffron)] px-2.5 py-1.5 text-xs text-white hover:bg-[var(--saffron-dark)] disabled:opacity-50">
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
              </button>
            </div>
          )}
          <div className="mt-3 max-h-[60vh] space-y-1 overflow-auto">
            {entities.map((e) => (
              <button key={e.slug} onClick={() => open(e)} data-testid={`mapentity-${e.slug}`}
                className={`block w-full truncate border px-3 py-2 text-left text-sm transition-colors ${sel?.slug === e.slug && sel?.type === e.type ? "border-saffron bg-saffron/10 text-ink" : "border-line text-ink-2 hover:border-saffron"}`}>
                {e.name}
              </button>
            ))}
            {entities.length === 0 && <p className="py-6 text-center text-sm text-ink-3">No entities.</p>}
          </div>
        </div>

        {/* Link editor */}
        <div className="border border-line bg-surface p-5">
          {!sel ? (
            <p className="py-20 text-center text-ink-3">Select an entity on the left to manage its mappings.</p>
          ) : loading ? (
            <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-saffron" /></div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-3">{types.find((t) => t.type === sel.type)?.label}</p>
                  {renaming ? (
                    <div className="mt-1 flex items-center gap-1.5">
                      <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRename()} data-testid="map-rename-input"
                        className="border border-line bg-parchment px-2 py-1 text-lg text-ink focus:border-saffron focus:outline-none" />
                      <button onClick={doRename} data-testid="map-rename-save" className="inline-flex items-center gap-1 bg-[var(--saffron)] px-2 py-1.5 text-xs text-white hover:bg-[var(--saffron-dark)]"><Check className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setRenaming(false)} className="text-xs text-ink-3 hover:text-saffron">Cancel</button>
                    </div>
                  ) : (
                    <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink">
                      {sel.name}
                      {EDITABLE.has(sel.type) && (
                        <button onClick={() => { setRenameVal(sel.name); setRenaming(true); }} data-testid="map-rename-btn" title="Rename across the platform" className="text-ink-3 hover:text-saffron"><Pencil className="h-3.5 w-3.5" /></button>
                      )}
                    </h2>
                  )}
                </div>
                <button onClick={save} disabled={saving} data-testid="map-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
              </div>

              <div className="space-y-4">
                {otherTypes.map((ot) => (
                  <div key={ot.type} className="border border-line p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-ink">{ot.label}</span>
                      <button onClick={() => loadPicker(ot.type, "")} data-testid={`map-add-${ot.type}`} className="inline-flex items-center gap-1 text-xs text-saffron hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(links[ot.type] || []).map((x) => (
                        <span key={x.slug} className="inline-flex items-center gap-1 border border-saffron/50 bg-saffron/10 px-2 py-1 text-xs text-ink">
                          {x.name}
                          <button onClick={() => removeLink(ot.type, x.slug)} data-testid={`map-remove-${x.slug}`}><X className="h-3 w-3 text-ink-3 hover:text-saffron" /></button>
                        </span>
                      ))}
                      {(links[ot.type] || []).length === 0 && <span className="text-xs text-ink-3">None linked.</span>}
                    </div>
                    {picker.type === ot.type && (
                      <div className="mt-2 border border-line bg-parchment p-2">
                        <input autoFocus value={picker.q} onChange={(e) => loadPicker(ot.type, e.target.value)} placeholder={`Search ${ot.label}…`} data-testid="map-picker-search"
                          className="w-full border border-line bg-surface px-2 py-1.5 text-sm focus:border-saffron focus:outline-none" />
                        <div className="mt-2 max-h-48 space-y-1 overflow-auto">
                          {picker.items.map((it) => (
                            <button key={it.slug} onClick={() => addLink(ot.type, it)} data-testid={`map-pick-${it.slug}`} className="block w-full truncate border border-line px-2 py-1 text-left text-xs text-ink-2 hover:border-saffron hover:text-saffron">{it.name}</button>
                          ))}
                        </div>
                        <button onClick={() => setPicker({ type: "", q: "", items: [] })} className="mt-2 text-xs text-ink-3 hover:text-saffron">Close</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
